/**
 * PostPurchaseConfig — skill de recompra (flowType POST_PURCHASE).
 *
 * Un cron diario del backend, a las 11:00 hora de la cuenta, pide a la tienda los
 * pedidos de hace N días y manda una plantilla de marketing a los que siguen en pie.
 * Los límites de aquí son los de `PostPurchaseSkill` en lixsa-conversation: si el
 * backend recibe algo fuera de rango usa el valor por defecto en silencio.
 *
 * @module PostPurchaseConfig
 */

import { PLATFORMS } from '@/components/Skills/LixsaSkills/ThanksOrder/Utils/PlatformRegistry';

export const POST_PURCHASE_SKILL_ID = '942ab48e-0edc-48dc-a2d5-2b38d9b2f0af';

export const DEFAULT_DELAY_DAYS = 10;
export const MIN_DELAY_DAYS = 1;
export const MAX_DELAY_DAYS = 180;

/** Hora local de la cuenta a la que sale el mensaje. */
export const SEND_HOUR = '11:00';

/** Tiendas a las que el backend sabe pedir pedidos por fecha. */
export const SUPPORTED_PLATFORM_IDS = [
    PLATFORMS.shopify.id,
    PLATFORMS.woocommerce.id,
    PLATFORMS.prestashop.id,
    PLATFORMS.tiendanube.id,
    PLATFORMS.magento.id,
];

/**
 * @param {number|string} value
 * @returns {boolean}
 */
export function isValidDelayDays(value) {
    const days = Number(value);
    return Number.isInteger(days) && days >= MIN_DELAY_DAYS && days <= MAX_DELAY_DAYS;
}

/**
 * Mismo criterio que el backend: vacío, no numérico o fuera de rango → por defecto.
 *
 * @param {number|string|null|undefined} value Valor crudo de `config_values.delay_days`.
 * @returns {number}
 */
export function parseDelayDays(value) {
    if (value === null || value === undefined || String(value).trim() === '') return DEFAULT_DELAY_DAYS;
    const days = Number(String(value).trim());
    return isValidDelayDays(days) ? days : DEFAULT_DELAY_DAYS;
}

/**
 * Variables que rellena `FlowMessagePostPurchase`. Una variable que no esté aquí sale
 * vacía, por eso la lista es cerrada. Las etiquetas reutilizan las de Thanks Order y
 * carrito abandonado, que ya tienen estos mismos nombres.
 *
 * `customer_name` lleva solo el nombre de pila y `cart_price` va con la moneda detrás.
 *
 * @type {Array<{label: string, i18nKey: string, value: string, example: string}>}
 */
export const POST_PURCHASE_VARIABLES = [
    {
        label: 'customer_name',
        i18nKey: 'thanksOrder.variables.customerName',
        value: '{{customer_name}}',
        example: 'Susana',
    },
    { label: 'agent_name', i18nKey: 'thanksOrder.variables.agentName', value: '{{agent_name}}', example: 'Lixsa' },
    {
        label: 'shop_name',
        i18nKey: 'skills.abandonCart.variables.shopName',
        value: '{{shop_name}}',
        example: 'Mi Tienda Online',
    },
    {
        label: 'order_number',
        i18nKey: 'thanksOrder.variables.orderNumber',
        value: '{{order_number}}',
        example: '#10432',
    },
    {
        label: 'product_list',
        i18nKey: 'skills.abandonCart.variables.productList',
        value: '{{product_list}}',
        example: 'Camiseta Premium, Zapatillas Running',
    },
    {
        label: 'cart_price',
        i18nKey: 'skills.abandonCart.variables.cartPrice',
        value: '{{cart_price}}',
        example: '149.99 EUR',
    },
];
